import type Game from '../game';
import type Entity from '../entity/entity';

export default class Minimap {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;

    // Off-screen canvas where we pre-render terrain tiles
    private terrainCanvas: HTMLCanvasElement;
    private terrainCtx: CanvasRenderingContext2D;

    // Off-screen 1x1 canvas used to sample the average colour of a tile
    private sampleCanvas: HTMLCanvasElement;
    private sampleCtx: CanvasRenderingContext2D;

    private size = 160;
    private radiusPx = 76;
    private radiusTiles = 22;
    private expanded = false;

    // Cache terrain when player moves to a new tile
    private lastGridX = -1;
    private lastGridY = -1;

    // Colour cache so we only sample each tileId once
    private tileColourCache = new Map<number, string>();

    public constructor(private game: Game) {
        this.canvas = document.querySelector('#minimap')!;
        this.ctx = this.canvas.getContext('2d')!;

        this.terrainCanvas = document.createElement('canvas');
        this.terrainCanvas.width = this.size;
        this.terrainCanvas.height = this.size;
        this.terrainCtx = this.terrainCanvas.getContext('2d')!;

        this.sampleCanvas = document.createElement('canvas');
        this.sampleCanvas.width = 1;
        this.sampleCanvas.height = 1;
        this.sampleCtx = this.sampleCanvas.getContext('2d')!;

        this.bindButtons();
    }

    private bindButtons(): void {
        let container = document.querySelector<HTMLElement>('#minimap-container')!;

        container.querySelector('#minimap-zoom-in')?.addEventListener('click', () => {
            this.setRadius(Math.max(8, this.radiusTiles - 4));
        });

        container.querySelector('#minimap-zoom-out')?.addEventListener('click', () => {
            this.setRadius(Math.min(60, this.radiusTiles + 4));
        });

        container.querySelector('#minimap-expand')?.addEventListener('click', () => {
            this.toggleExpand();
        });
    }

    private setRadius(tiles: number): void {
        this.radiusTiles = tiles;
        this.lastGridX = -1;
        this.lastGridY = -1;
    }

    private toggleExpand(): void {
        let container = document.querySelector<HTMLElement>('#minimap-container')!;

        this.expanded = !this.expanded;
        container.classList.toggle('minimap-expanded', this.expanded);

        this.lastGridX = -1;
        this.lastGridY = -1;
    }

    public render(): void {
        let { player, entities, pointer } = this.game;
        if (!player?.name) return;

        let { ctx, size, radiusPx, radiusTiles, terrainCanvas, lastGridX, lastGridY } = this,
            { gridX: gx, gridY: gy, instance: pid } = player,
            cx = size / 2,
            cy = size / 2,
            scale = radiusPx / radiusTiles;

        // Re-render terrain only when player moves to a new tile
        if (gx !== lastGridX || gy !== lastGridY) {
            this.renderTerrain(gx, gy, cx, cy, scale);
            this.lastGridX = gx;
            this.lastGridY = gy;
        }

        ctx.clearRect(0, 0, size, size);

        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, radiusPx, 0, Math.PI * 2);
        ctx.clip();

        // Draw cached terrain
        ctx.drawImage(terrainCanvas, 0, 0);

        // Draw entities
        entities.forEachEntity((entity: Entity) => {
            if (!entity || entity.instance === pid) return;

            let dx = entity.gridX - gx,
                dy = entity.gridY - gy;

            if (dx * dx + dy * dy > radiusTiles * radiusTiles) return;

            let color = this.colorFor(entity);
            if (!color) return;

            let px = cx + dx * scale,
                py = cy + dy * scale;

            ctx.beginPath();
            ctx.fillStyle = color;
            ctx.arc(px, py, 2.5, 0, Math.PI * 2);
            ctx.fill();
        });

        // Quest target marker (pulsing golden star)
        let { questTarget } = pointer;

        if (questTarget) {
            let qdx = questTarget.gridX - gx,
                qdy = questTarget.gridY - gy,
                qpx = cx + qdx * scale,
                qpy = cy + qdy * scale,
                pulse = 0.6 + 0.4 * Math.sin(Date.now() / 300),
                qr = 4 * pulse;

            ctx.save();
            ctx.shadowColor = '#ffd700';
            ctx.shadowBlur = 6 * pulse;
            ctx.beginPath();
            this.drawStar(ctx, qpx, qpy, 4, qr, qr * 0.4);
            ctx.fillStyle = `rgba(255, 215, 0, ${0.7 + 0.3 * pulse})`;
            ctx.fill();
            ctx.restore();
        }

        // Player dot
        ctx.beginPath();
        ctx.fillStyle = '#fff';
        ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
        ctx.fill();

        // Crosshair
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(cx, cy - 8);
        ctx.lineTo(cx, cy + 8);
        ctx.moveTo(cx - 8, cy);
        ctx.lineTo(cx + 8, cy);
        ctx.stroke();

        ctx.restore();
    }

    private renderTerrain(gx: number, gy: number, cx: number, cy: number, scale: number): void {
        let { terrainCtx, size, radiusTiles, radiusPx, game } = this,
            { map } = game,
            r = Math.ceil(radiusTiles);

        terrainCtx.clearRect(0, 0, size, size);

        // Dark fallback background
        terrainCtx.fillStyle = 'rgba(15, 25, 15, 0.85)';
        terrainCtx.fillRect(0, 0, size, size);

        for (let dy = -r; dy <= r; dy++)
            for (let dx = -r; dx <= r; dx++) {
                if (dx * dx + dy * dy > radiusTiles * radiusTiles) continue;

                let tx = gx + dx,
                    ty = gy + dy;

                if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) continue;

                let index = map.coordToIndex(tx, ty),
                    tile = map.data[index];

                if (!tile) continue;

                let tileId = this.resolveTileId(tile),
                    colour = this.getTileColour(tileId);

                if (!colour) continue;

                let px = cx + dx * scale - scale / 2,
                    py = cy + dy * scale - scale / 2;

                terrainCtx.fillStyle = colour;
                terrainCtx.fillRect(px, py, scale + 0.5, scale + 0.5);
            }

        // Collision overlay
        for (let dy = -r; dy <= r; dy++)
            for (let dx = -r; dx <= r; dx++) {
                if (dx * dx + dy * dy > radiusTiles * radiusTiles) continue;

                let tx = gx + dx,
                    ty = gy + dy;

                if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) continue;

                if (!map.grid[ty]?.[tx]) continue;

                let px = cx + dx * scale - scale / 2,
                    py = cy + dy * scale - scale / 2;

                terrainCtx.fillStyle = 'rgba(0, 0, 0, 0.25)';
                terrainCtx.fillRect(px, py, scale + 0.5, scale + 0.5);
            }

        // Fog-of-war fade at the edges
        let gradient = terrainCtx.createRadialGradient(cx, cy, radiusPx * 0.65, cx, cy, radiusPx);

        gradient.addColorStop(0, 'rgba(0,0,0,0)');
        gradient.addColorStop(1, 'rgba(0,0,0,0.6)');
        terrainCtx.fillStyle = gradient;
        terrainCtx.fillRect(0, 0, size, size);
    }

    private resolveTileId(tile: unknown): number {
        if (typeof tile === 'number') return tile;

        // Array of layers: use the last (topmost) valid one
        if (Array.isArray(tile)) {
            for (let i = tile.length - 1; i >= 0; i--) {
                let t = tile[i];

                if (typeof t === 'number' && t > 0) return t;
                if (t && typeof t === 'object' && 'tileId' in t)
                    return (t as { tileId: number }).tileId;
            }

            return 0;
        }

        // RotatedTile
        if (tile && typeof tile === 'object' && 'tileId' in tile)
            return (tile as { tileId: number }).tileId;

        return 0;
    }

    private getTileColour(tileId: number): string | null {
        if (tileId <= 0) return null;

        let cached = this.tileColourCache.get(tileId);

        if (cached !== undefined) return cached;

        let { map } = this.game,
            tileset = map.getTilesetFromId(tileId);

        if (!tileset?.complete) {
            this.tileColourCache.set(tileId, '#1a2a1a');
            return '#1a2a1a';
        }

        let { tileSize } = map,
            setWidth = tileset.width / tileSize,
            relative = tileId - tileset.firstGid,
            srcX = (relative % setWidth) * tileSize,
            srcY = Math.floor(relative / setWidth) * tileSize;

        // Sample center pixel of the tile
        this.sampleCtx.clearRect(0, 0, 1, 1);
        this.sampleCtx.drawImage(tileset, srcX, srcY, tileSize, tileSize, 0, 0, 1, 1);

        let { data } = this.sampleCtx.getImageData(0, 0, 1, 1),
            colour = data[3] < 30 ? null : `rgb(${data[0]},${data[1]},${data[2]})`;

        this.tileColourCache.set(tileId, colour || '#1a2a1a');
        return colour || '#1a2a1a';
    }

    private colorFor(entity: Entity): string | null {
        if (entity.isMob()) return '#ff4d4d';
        if (entity.isPlayer()) return '#ffe066';
        if (entity.isNPC()) return '#5cc7ff';
        if (entity.isItem?.()) return '#aaaaaa';
        return null;
    }

    private drawStar(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        points: number,
        outer: number,
        inner: number
    ): void {
        let step = Math.PI / points;

        ctx.moveTo(x, y - outer);

        for (let i = 0; i < 2 * points; i++) {
            let r = i % 2 === 0 ? outer : inner,
                angle = i * step - Math.PI / 2;

            ctx.lineTo(x + Math.cos(angle) * r, y + Math.sin(angle) * r);
        }

        ctx.closePath();
    }
}
